import React, { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
    Box,
    Paper,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Avatar,
    Typography,
    Skeleton,
    Alert,
} from '@mui/material';
import { OpenInNew as OpenInNewIcon, SwipeLeft as SwipeLeftIcon } from '@mui/icons-material';
import ShortFormDetailLayout from '../components/ShortFormDetailLayout';
import apiService from '../services/api';
import { ROUTES_NAMES } from '../components/Layout';
import { useBrandExplorerStore } from '../stores/brandExplorerStore';
import { useTrackerStore } from '../stores/trackerStore';
import { useAuth } from '../contexts/AuthContext';
import { getS3Url, ensureDirectS3Url } from '../utils/imageUtils';

const formatNumber = (num: number): string => {
    return new Intl.NumberFormat('es-ES').format(num);
};

type SortKey = 'title' | 'publishedAt' | 'views' | 'likes' | 'comments';

const BrandExplorerYoutubeShortsCreatorDetail: React.FC = () => {
    const { channelId } = useParams<{ channelId: string }>();
    const navigate = useNavigate();
    const { selectedBrand } = useBrandExplorerStore();
    const { startDate: startDateParam, endDate: endDateParam } = useTrackerStore();
    const { getCompanyTrackerBrandId } = useAuth();
    const brandId = getCompanyTrackerBrandId() || selectedBrand?._id || null;

    const { data: brandData, isLoading, error } = useQuery({
        queryKey: ['brand-data', brandId, startDateParam, endDateParam],
        queryFn: () => apiService.getBrandData(brandId!, undefined, undefined, startDateParam!, endDateParam!),
        enabled: !!brandId && !!channelId,
        staleTime: 10 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
    });

    const shortFormMentions = useMemo(() => {
        if (!brandData?.periods) return [];
        const filterMentions = (mention: any) => {
            if ((mention.contentForm || 'short') !== 'short') return false;
            const date = new Date(mention.content?.publishedAt);
            if (startDateParam && endDateParam) {
                const dateStr = date.toISOString().slice(0, 10);
                return dateStr >= startDateParam && dateStr <= endDateParam;
            }
            return true;
        };
        return brandData.periods.flatMap((y: any) => y.mentions).filter(filterMentions);
    }, [brandData, startDateParam, endDateParam]);

    const channelMentions = useMemo(() => {
        if (!channelId) return [];
        const ytShort = shortFormMentions.filter(
            (m: any) =>
                m.content?.source === 'youtube' &&
                m.content?.mediaType === 'short' &&
                (String(m.podcast?._id ?? '') === channelId || (m.podcast?._id?.toString?.() ?? '') === channelId)
        );
        return ytShort;
    }, [shortFormMentions, channelId]);

    const channelInfo = useMemo(() => {
        const first = channelMentions[0];
        if (!first?.podcast) return { channelName: 'YouTube Channel', imageUrl: undefined, imageS3Key: undefined };
        return {
            channelName: first.podcast.title ?? 'YouTube Channel',
            imageUrl: first.podcast.imageUrl,
            imageS3Key: first.podcast.imageS3Key,
        };
    }, [channelMentions]);

    const metrics = useMemo(() => {
        if (channelMentions.length === 0) return null;
        const totalShorts = channelMentions.length;
        const totalViews = channelMentions.reduce((s: number, m: any) => s + (m.content?.views ?? 0), 0);
        const totalLikes = channelMentions.reduce((s: number, m: any) => s + (m.content?.likes ?? 0), 0);
        const totalComments = channelMentions.reduce((s: number, m: any) => s + (m.content?.comments ?? 0), 0);
        return { totalShorts, totalViews, totalLikes, totalComments };
    }, [channelMentions]);

    const handleBack = () => {
        navigate(ROUTES_NAMES.TRACKER);
    };

    const [orderBy, setOrderBy] = useState<SortKey>('publishedAt');
    const [order, setOrder] = useState<'asc' | 'desc'>('desc');

    const handleSort = (key: SortKey) => {
        const isAsc = orderBy === key && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(key);
    };

    const handleContentClick = (contentId: string, content: any, contentMentions: any[]) => {
        const route = ROUTES_NAMES.TRACKER_SHORTFORM_CONTENT.replace(':contentId', contentId);
        navigate(route, { state: { content, mentions: contentMentions } });
    };

    if (!channelId) {
        return (
            <ShortFormDetailLayout
                onBack={handleBack}
                avatar={<Avatar />}
                title="YouTube Shorts"
                resumenTitle="Resumen"
                cards={[]}
                formatNumber={formatNumber}
                contentChildren={<Alert severity="error">Missing channel.</Alert>}
            />
        );
    }

    if (isLoading && !channelMentions.length) {
        return (
            <ShortFormDetailLayout
                onBack={handleBack}
                avatar={<Skeleton variant="circular" width={56} height={56} />}
                title={<Skeleton width={200} />}
                resumenTitle="Resumen"
                cards={[]}
                formatNumber={formatNumber}
                contentChildren={<Skeleton variant="rectangular" height={200} />}
            />
        );
    }

    if (error) {
        return (
            <ShortFormDetailLayout
                onBack={handleBack}
                avatar={<Avatar />}
                title="YouTube Shorts"
                resumenTitle="Resumen"
                cards={[]}
                formatNumber={formatNumber}
                contentChildren={<Alert severity="error">Failed to load data.</Alert>}
            />
        );
    }

    const subtitleParts = ['YouTube Shorts'];
    if (startDateParam && endDateParam) subtitleParts.push(`${startDateParam} – ${endDateParam}`);
    const subtitleLine = subtitleParts.join(' · ');
    const avatarSrc = channelInfo.imageS3Key ? ensureDirectS3Url(getS3Url(channelInfo.imageS3Key)) : channelInfo.imageUrl ?? null;

    return (
        <ShortFormDetailLayout
            onBack={handleBack}
            avatar={
                avatarSrc ? (
                    <Avatar src={avatarSrc} sx={{ width: 56, height: 56 }} />
                ) : (
                    <Avatar sx={{ width: 56, height: 56 }}>{channelInfo.channelName?.charAt(0)?.toUpperCase() ?? '?'}</Avatar>
                )
            }
            title={channelInfo.channelName}
            subtitle={subtitleLine ? <Typography variant="body2" color="text.secondary">{subtitleLine}</Typography> : undefined}
            resumenTitle="Resumen"
            cards={
                metrics
                    ? [
                        { value: metrics.totalShorts, label: 'Shorts' },
                        { value: metrics.totalViews, label: 'Views' },
                        { value: metrics.totalLikes, label: 'Likes' },
                        { value: metrics.totalComments, label: 'Comentarios' },
                    ]
                    : []
            }
            formatNumber={formatNumber}
            contentTitle="YouTube Shorts"
            contentChildren={
                channelMentions.length > 0 ? (
                    (() => {
                        const deduped = Array.from(
                            new Map(channelMentions.map((m: any) => [m.content?._id?.toString?.() ?? m.content?._id, m])).values()
                        );
                        const getValue = (m: any, key: SortKey): number | string => {
                            const c = m.content;
                            switch (key) {
                                case 'title': return (c?.title ?? '').toString().toLowerCase();
                                case 'publishedAt': return c?.publishedAt ? new Date(c.publishedAt).getTime() : 0;
                                case 'views': return c?.views ?? 0;
                                case 'likes': return c?.likes ?? 0;
                                case 'comments': return c?.comments ?? 0;
                                default: return 0;
                            }
                        };
                        const sorted = [...deduped].sort((a, b) => {
                            const aVal = getValue(a, orderBy);
                            const bVal = getValue(b, orderBy);
                            const cmp = typeof aVal === 'number' && typeof bVal === 'number'
                                ? aVal - bVal
                                : String(aVal).localeCompare(String(bVal));
                            return order === 'asc' ? cmp : -cmp;
                        });
                        return (
                            <TableContainer component={Paper} variant="outlined">
                                <Box
                                    sx={{
                                        display: { xs: 'flex', md: 'none' },
                                        alignItems: 'center',
                                        gap: 1,
                                        px: 2,
                                        py: 1,
                                        color: 'text.secondary',
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                >
                                    <SwipeLeftIcon />
                                    <Typography variant="caption">Desliza para ver más</Typography>
                                </Box>
                                <Table sx={{ tableLayout: 'fixed' }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell align="left" sortDirection={orderBy === 'title' ? order : false} sx={{ width: '40%', display: { xs: 'none', sm: 'table-cell' }, padding: { xs: '12px', sm: '16px' } }}>
                                                <TableSortLabel active={orderBy === 'title'} direction={orderBy === 'title' ? order : 'asc'} onClick={() => handleSort('title')}>
                                                    Contenido
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell align="right" sortDirection={orderBy === 'publishedAt' ? order : false} sx={{ display: { xs: 'none', sm: 'table-cell' }, padding: { xs: '12px', sm: '16px' } }}>
                                                <TableSortLabel active={orderBy === 'publishedAt'} direction={orderBy === 'publishedAt' ? order : 'asc'} onClick={() => handleSort('publishedAt')}>
                                                    Published
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell align="right" sx={{ display: { xs: 'table-cell', sm: 'none' }, padding: { xs: '12px', sm: '16px' } }}>
                                                Interactions
                                            </TableCell>
                                            <TableCell align="right" sortDirection={orderBy === 'views' ? order : false} sx={{ padding: { xs: '12px', sm: '16px' } }}>
                                                <TableSortLabel active={orderBy === 'views'} direction={orderBy === 'views' ? order : 'asc'} onClick={() => handleSort('views')}>
                                                    Views
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell align="right" sortDirection={orderBy === 'likes' ? order : false} sx={{ display: { xs: 'none', sm: 'table-cell' }, padding: { xs: '12px', sm: '16px' } }}>
                                                <TableSortLabel active={orderBy === 'likes'} direction={orderBy === 'likes' ? order : 'asc'} onClick={() => handleSort('likes')}>
                                                    Likes
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell align="right" sortDirection={orderBy === 'comments' ? order : false} sx={{ display: { xs: 'none', sm: 'table-cell' }, padding: { xs: '12px', sm: '16px' } }}>
                                                <TableSortLabel active={orderBy === 'comments'} direction={orderBy === 'comments' ? order : 'asc'} onClick={() => handleSort('comments')}>
                                                    Comments
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell align="right" sx={{ width: '50px', padding: { xs: '8px', sm: '12px' } }}>
                                                Link
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {sorted.map((m: any) => {
                                            const cId = m.content?._id?.toString?.();
                                            const content = m.content;
                                            const contentMentions = channelMentions.filter(
                                                (x: any) => (x.content?._id?.toString?.() ?? x.content?._id) === cId
                                            );
                                            return (
                                                <TableRow
                                                    key={m._id}
                                                    hover
                                                    onClick={() => cId && content && handleContentClick(cId, content, contentMentions)}
                                                    sx={{ cursor: cId ? 'pointer' : 'default' }}
                                                >
                                                    <TableCell sx={{ width: '40%', overflow: 'hidden', display: { xs: 'none', sm: 'table-cell' }, padding: { xs: '12px', sm: '16px' } }} align="left">
                                                        <Box sx={{ width: '100%', overflow: 'hidden' }}>
                                                            <Typography variant="body2" fontWeight="medium" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%', display: 'block' }}>
                                                                {content?.title ?? '-'}
                                                            </Typography>
                                                            {m.context && (
                                                                <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%', display: 'block' }}>
                                                                    {m.context}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, padding: { xs: '12px', sm: '16px' } }}>
                                                        <Typography variant="caption">
                                                            {content?.publishedAt ? new Date(content.publishedAt).toLocaleDateString() : '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ display: { xs: 'table-cell', sm: 'none' }, padding: { xs: '12px', sm: '16px' } }}>
                                                        <Typography variant="body2">{formatNumber((content?.likes ?? 0) + (content?.comments ?? 0))}</Typography>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ padding: { xs: '12px', sm: '16px' } }}>
                                                        <Typography variant="body2">{formatNumber(content?.views ?? 0)}</Typography>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, padding: { xs: '12px', sm: '16px' } }}>
                                                        <Typography variant="body2">{formatNumber(content?.likes ?? 0)}</Typography>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, padding: { xs: '12px', sm: '16px' } }}>
                                                        <Typography variant="body2">{formatNumber(content?.comments ?? 0)}</Typography>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ width: '50px', padding: { xs: '8px', sm: '12px' } }} onClick={(e) => e.stopPropagation()}>
                                                        {content?.url ? (
                                                            <IconButton size="small" onClick={() => window.open(content.url, '_blank')} sx={{ color: 'primary.main' }} title="Abrir enlace">
                                                                <OpenInNewIcon fontSize="small" />
                                                            </IconButton>
                                                        ) : (
                                                            <Typography variant="caption" color="text.disabled">Sin enlace</Typography>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        );
                    })()
                ) : (
                    <Paper variant="outlined" sx={{ p: 3 }}>
                        <Typography color="text.secondary">No hay YouTube Shorts para este canal.</Typography>
                    </Paper>
                )
            }
        />
    );
};

export default BrandExplorerYoutubeShortsCreatorDetail;
